import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { SharedAPI, UserApi } from '@shared-features/services';
import { RoleAPI } from '@shared-features/services/role-api.service';
import { Country } from '@shared-features/models';
import { UserType } from '@shared-features/enums/shared-enums.enum';
import { User } from '@features/users/models/user.model';
import { ContactPerson } from '@features/receivers/models/contact-person.model';
import { noWhitespaceValidator } from '@shared/utils/form-validators.util';

export interface AddContactDialogData {
  customerId: number;
}

@Component({
  selector: 'app-add-contact',
  templateUrl: './add-contact.component.html',
  styleUrls: ['./add-contact.component.scss'],
})
export class AddContactComponent extends BaseComponent implements OnInit {
  form: FormGroup;
  countries: Country[] = [];
  private defaultRoleId: number;

  get contactsArray(): FormArray {
    return this.form.get('contacts') as FormArray;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddContactDialogData,
    private dialogRef: MatDialogRef<AddContactComponent, ContactPerson[]>,
    private formBuilder: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private sharedAPI: SharedAPI,
    private roleAPI: RoleAPI,
    private userApi: UserApi
  ) {
    super();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      contacts: this.formBuilder.array([this.newContactRow()]),
    });

    this.load(forkJoin([this.sharedAPI.getAllCountries(), this.roleAPI.getActiveRoles(UserType.CUSTOMER)]))
      .pipe(takeUntil(this.destroy$))
      .subscribe(([countries, roles]) => {
        // Loaded once here and handed to every row's phone field, so N rows don't fire N requests.
        // The field seeds its own Saudi default off this list.
        this.countries = countries;
        this.defaultRoleId = (roles.find((role) => /contact/i.test(role.name)) ?? roles[0])?.id;
        this.changeDetectorRef.markForCheck();
      });
  }

  // app-phone-number-field owns the country-code/number pair and its validators, so a row is just
  // a name plus that one control.
  private newContactRow(): FormGroup {
    return this.formBuilder.group({
      fullName: ['', [Validators.required, noWhitespaceValidator]],
      phone: [null],
    });
  }

  addAnotherContact(): void {
    this.contactsArray.push(this.newContactRow());
  }

  removeContact(index: number): void {
    this.contactsArray.removeAt(index);
  }

  onConfirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const requests = this.contactsArray.value.map(({ fullName, phone }) => {
      const [firstName, ...rest] = fullName.trim().split(/\s+/);
      const user = Object.assign(new User(), {
        firstName,
        lastName: rest.join(' ') || firstName,
        mobileNumber: `${phone?.countryCode}-${phone?.mobileNumber}`,
      });
      return this.userApi.addUser(
        user,
        UserType.CUSTOMER,
        this.data?.customerId,
        this.defaultRoleId ? [this.defaultRoleId] : []
      );
    });

    this.load(forkJoin(requests), { successMessagePath: 'SuccessMessages.UserAddedSuccessfully', isLoadingTransparent: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe((users) => {
        this.dialogRef.close(users as unknown as ContactPerson[]);
      });
  }
}
